import { MutableRefObject, useEffect, useRef } from 'react'

export type Direction = 'up' | 'down' | 'left' | 'right'
export type Position = [number, number]
export type Options = {
  /**
   * CSS className that is applied to the HTMLDivElement when swipping is started and stopped
   */
  swippingClassName?: string

  /**
   * Invoked when onTouchStart & onTouchMove are called
   *
   * If this function returns false, onTouchStart || onTouchMove will not run
   *
   * @returns boolean
   */
  checkCanStartSwipe?: (e: TouchEvent) => boolean
  checkCanContinueSwipe?: (e: TouchEvent, direction: Direction | null) => boolean

  onTouchStart?: () => void
  onTouchMove?: (
    dragOffset: number,
    direction: Direction,
    currentPosition: Position,
    timeSinceSwipeStart: number,
  ) => void
  onTouchEnd?: (dragOffset: number, endPosition: Position, swipeSpeed: number, direction: Direction) => void
}

export const useSwipeEffects = (
  swipeDirection: 'horizontal' | 'vertical', // Determins whether or not we invoke onTouchMove based on passing our jitter threshold
  options?: Options,
) => {
  const swipeRef = useRef<HTMLDivElement | null>(null)

  const dragDirection = useRef<Direction | null>(null)
  const dragOffset = useRef<Position | null>(null)

  const startingTouchPosition = useRef<Position | null>(null)
  const startingTouchTime = useRef<number | null>(null)

  const axisOfMotion = swipeDirection === 'horizontal' ? 0 : 1
  const staticAxis = swipeDirection === 'horizontal' ? 1 : 0

  const JITTER_THRESHOLD = 25 // Threshold to ignore all swipes until past this point

  const handleTouchStart = (touchEvent: TouchEvent) => {
    if (options?.checkCanStartSwipe && !options.checkCanStartSwipe(touchEvent)) return

    startingTouchPosition.current = [touchEvent.touches[0]?.clientX, touchEvent.touches[0]?.clientY]
    startingTouchTime.current = touchEvent.timeStamp

    if (options?.onTouchStart) options.onTouchStart()
  }

  const handleTouchMove = (touchEvent: TouchEvent) => {
    if (startingTouchPosition.current === null) return

    const currentX = touchEvent.touches[0]?.clientX
    const currentY = touchEvent.touches[0]?.clientY

    const currentOnAxis = swipeDirection === 'horizontal' ? currentX : currentY
    const currentSwipeDirection = getSwipeDirection(startingTouchPosition.current[axisOfMotion], currentOnAxis)

    if (options?.checkCanContinueSwipe && !options.checkCanContinueSwipe(touchEvent, currentSwipeDirection))
      return handleResetState()

    // If they reverse direction, treat the swipe as if it started at the reverse point
    if (dragDirection.current && dragDirection.current !== currentSwipeDirection) {
      startingTouchPosition.current = [currentX, currentY]
      startingTouchTime.current = touchEvent.timeStamp
    }

    dragOffset.current = [currentX - startingTouchPosition.current[0], startingTouchPosition.current[1] - currentY]

    // If we haven't met our threshold AND haven't started dragging yet.
    if (Math.abs(dragOffset.current[axisOfMotion]) < JITTER_THRESHOLD && !dragDirection.current) return

    // If we've moved too far in the opposite direction, don't treat this as a drag in the other direction
    if (Math.abs(dragOffset.current[staticAxis]) > JITTER_THRESHOLD && !dragDirection.current) return

    touchEvent.preventDefault()

    // Once we start swiping don't allow them to change directions
    if (!dragDirection.current) dragDirection.current = currentSwipeDirection

    document.body.classList.add('swipping')

    // Note that this number can be negative IF they change directions
    const timeSinceSwipeStarted = startingTouchTime.current ? touchEvent.timeStamp - startingTouchTime.current : 0

    if (options?.swippingClassName) swipeRef.current?.classList.add(options.swippingClassName)
    if (options?.onTouchMove && dragDirection.current)
      options.onTouchMove(
        dragOffset.current[axisOfMotion] + getJitterOffset(),
        dragDirection.current,
        dragOffset.current,
        timeSinceSwipeStarted < 0 ? 0 : timeSinceSwipeStarted, // If they change directions, and we reset the starting time, just return 0. Don't return negative time lol
      )
  }

  const handleTouchEnd = (touchEvent: TouchEvent) => {
    if (options?.swippingClassName) swipeRef.current?.classList.remove(options.swippingClassName) // Alaways remove this

    if (dragOffset.current === null || startingTouchTime.current === null || !dragDirection.current) return

    // NOTE - absolute value because speed is directionless.
    const swipeSpeed = Math.abs(dragOffset.current[axisOfMotion]) / (touchEvent.timeStamp - startingTouchTime.current)

    document.body.classList.remove('swipping')

    if (options?.onTouchEnd)
      options.onTouchEnd(
        dragOffset.current[axisOfMotion] + getJitterOffset(),
        dragOffset.current,
        swipeSpeed,
        dragDirection.current,
      )

    handleResetState()
  }

  const handleResetState = () => {
    // Always reset state once the drawer has entirely opened/ shut
    dragOffset.current = null
    startingTouchPosition.current = null
    startingTouchTime.current = null
    dragDirection.current = null
  }

  const getSwipeDirection = (previousPostion: number | null, currentPosition: number | null) => {
    if (previousPostion === null || currentPosition === null) return null

    if (swipeDirection === 'horizontal' && previousPostion > currentPosition) return 'left'
    if (swipeDirection === 'horizontal' && previousPostion < currentPosition) return 'right'

    if (swipeDirection === 'vertical' && previousPostion < currentPosition) return 'down'
    if (swipeDirection === 'vertical' && previousPostion > currentPosition) return 'up'

    return null
  }

  const getJitterOffset = () => {
    if (swipeDirection === 'horizontal' && dragDirection.current === 'right') return -1 * JITTER_THRESHOLD
    if (swipeDirection === 'horizontal' && dragDirection.current === 'left') return JITTER_THRESHOLD

    if (swipeDirection === 'vertical' && dragDirection.current === 'up') return -1 * JITTER_THRESHOLD
    if (swipeDirection === 'vertical' && dragDirection.current === 'down') return JITTER_THRESHOLD

    return 0
  }

  useEffect(() => {
    swipeRef.current?.addEventListener('touchstart', handleTouchStart)
    swipeRef.current?.addEventListener('touchmove', handleTouchMove, { passive: false })
    swipeRef.current?.addEventListener('touchend', handleTouchEnd)

    return () => {
      swipeRef.current?.removeEventListener('touchstart', handleTouchStart)
      swipeRef.current?.removeEventListener('touchmove', handleTouchMove)
      swipeRef.current?.removeEventListener('touchend', handleTouchEnd)
    }
  }, [swipeRef.current, swipeDirection, options])

  return swipeRef
}

type BackForthOptions = {
  onPrimaryTouchMove?: (
    dragOffset: number,
    direction: Direction,
    currentPosition: Position,
    timeSinceSwipeStart: number,
  ) => void
  onPrimaryTouchEnd?: (dragOffset: number, endPosition: Position, swipeSpeed: number, direction: Direction) => void

  onSecondaryTouchMove?: (
    dragOffset: number,
    direction: Direction,
    currentPosition: Position,
    timeSinceSwipeStart: number,
  ) => void
  onSecondaryTouchEnd?: (dragOffset: number, endPosition: Position, swipeSpeed: number, direction: Direction) => void

  skipDiplayChangesForSecondary?: boolean
} & Options

/**
 * Hooks that allows the user to swipe back and forth between an two elements in a "horizontal swipe row" esq fashion.
 * The logic is handled here, but this only works if the elements are setup correctly.
 *
 * To setup the elements correctly, this is expecting 2 elements both 100% width in a flex container with no wrapping.
 * Their parent needs overflow: hidden such that the second element is not on screen by default (you can give it display: none to improve performance, but it's not neccesary)
 *
 * This hook expects the first element to be on the left, and the second on the right. So you'll swipe ⬅️ to move the first element out and the second element in.
 * The opposite is true for reversing these properties.
 *
 * There is (currently) no mechanism to know which element is being shown via JS as this is a purely CSS opperation.
 *
 * @param swipeDirection
 * @param options
 *
 * @returns [Ref for first swipe element, ref for second swipe element, callbacks]
 */
export const useSwipeBackForthEffect = (
  options?: BackForthOptions | undefined,
): [
  MutableRefObject<HTMLDivElement | null>,
  MutableRefObject<HTMLDivElement | null>,
  {
    handleSwipeInPrimary: () => void // Also resets the CSS to default
    handleSwipeInSecondary: () => void
  },
] => {
  const primaryRef = useSwipeEffects('horizontal', {
    ...options,
    onTouchMove: (offset, direction, position, timeSinceSwipeStart) => {
      if (!primaryRef.current || !secondaryRef.current) return

      primaryRef.current.style.transition = 'unset'
      primaryRef.current.style.transform = `translateX(${offset > 0 ? 0 : offset}px)`

      secondaryRef.current.style.transition = 'unset'
      secondaryRef.current.style.transform = `translateX(${offset > 0 ? 0 : offset}px)`

      if (!options?.skipDiplayChangesForSecondary) secondaryRef.current.style.display = 'unset'

      if (options?.onPrimaryTouchMove) options.onPrimaryTouchMove(offset, direction, position, timeSinceSwipeStart)
    },
    onTouchEnd: (offset, position, speed, direction) => {
      if (!primaryRef.current || !secondaryRef.current) return

      const swipeThresholdMet = offset < -1 * (document.documentElement.clientWidth / 2) || (speed > 0.5 && offset < 0)

      primaryRef.current.style.transition = ''
      secondaryRef.current.style.transition = ''

      if (options?.onPrimaryTouchEnd) options.onPrimaryTouchEnd(offset, position, speed, direction)

      if (swipeThresholdMet) return handleSwipeInSecondary()

      primaryRef.current.style.transform = ''
      secondaryRef.current.style.transform = ''

      setTimeout(() => {
        if (!secondaryRef.current) return

        secondaryRef.current.style.display = ''
      }, 100)
    },
  })

  const secondaryRef = useSwipeEffects('horizontal', {
    ...options,
    onTouchMove: (offset, direction, position, timeSinceSwipeStart) => {
      if (!primaryRef.current || !secondaryRef.current) return

      primaryRef.current.style.transition = 'unset'
      primaryRef.current.style.transform = `translateX(calc(-100% + ${offset < 0 ? 0 : offset}px - 1rem))`

      secondaryRef.current.style.transition = 'unset'
      secondaryRef.current.style.transform = `translateX(calc(-100% + ${offset < 0 ? 0 : offset}px - 1rem))`

      if (options?.onSecondaryTouchMove) options.onSecondaryTouchMove(offset, direction, position, timeSinceSwipeStart)
    },
    onTouchEnd: (offset, position, speed, direction) => {
      if (!primaryRef.current || !secondaryRef.current) return

      const swipeThresholdMet = offset > document.documentElement.clientWidth / 2 || (speed > 0.5 && offset > 0)

      primaryRef.current.style.transition = ''
      secondaryRef.current.style.transition = ''

      if (options?.onSecondaryTouchEnd) options.onSecondaryTouchEnd(offset, position, speed, direction)

      if (swipeThresholdMet) return handleSwipeInPrimary()

      primaryRef.current.style.transform = 'translateX(calc(-100% - 1rem))'
      secondaryRef.current.style.transform = 'translateX(calc(-100% - 1rem))'
    },
  })

  const handleSwipeInPrimary = () => {
    if (!primaryRef.current || !secondaryRef.current) return

    primaryRef.current.style.transform = ''
    secondaryRef.current.style.transform = ''

    setTimeout(() => {
      if (!secondaryRef.current) return

      if (!options?.skipDiplayChangesForSecondary) secondaryRef.current.style.display = ''
    }, 200)
  }

  const handleSwipeInSecondary = () => {
    if (!primaryRef.current || !secondaryRef.current) return

    if (!options?.skipDiplayChangesForSecondary) secondaryRef.current.style.display = 'unset'

    setTimeout(() => {
      if (!primaryRef.current || !secondaryRef.current) return

      primaryRef.current.style.transform = 'translateX(calc(-100% - 1rem))'
      secondaryRef.current.style.transform = 'translateX(calc(-100% - 1rem))'
    }, 10)
  }

  return [primaryRef, secondaryRef, { handleSwipeInPrimary, handleSwipeInSecondary }]
}
